import {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  ElementType,
  ForwardedRef,
  forwardRef,
  ReactNode,
  MouseEvent as ReactMouseEvent,
} from "react"
import { cx } from "@emotion/css"
import type {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef,
  PolymorphicPropsWithRef,
} from "../utils/react-polymorphic-types"

import styles from "./styles"

const ButtonDefaultElement = "button"

type ButtonOwnProps = {
  color?: "default" | "primary" | "destructive" | "inherit"
  variant?: "default" | "contained" | "outlined"
  weight?: "default" | "normal"
  fullWidth?: boolean
  //
  startIcon?: ReactNode
  endIcon?: ReactNode
  //
  disabled?: boolean
}

export type ButtonProps<T extends ElementType = typeof ButtonDefaultElement> =
  PolymorphicPropsWithRef<ButtonOwnProps, T>

export const Button: PolymorphicForwardRefExoticComponent<
  ButtonOwnProps,
  typeof ButtonDefaultElement
> = forwardRef(function Button<
  T extends ElementType = typeof ButtonDefaultElement,
>(
  props: PolymorphicPropsWithoutRef<ButtonOwnProps, T>,
  ref: ForwardedRef<ElementRef<T>>,
) {
  const {
    as,
    //
    color = "default",
    variant = "default",
    weight,
    fullWidth = false,
    startIcon,
    endIcon,
    //
    children,
    //
    type = "button",
    disabled,
    onClick,
    //
    className,
    ...rest
  } = props

  const cn = cx(
    styles.base,
    styles.root,
    {
      [styles.fullWidth]: fullWidth,
    },
    className,
  )

  const Element: ElementType = as || ButtonDefaultElement

  const buttons_only_props: ComponentProps<typeof ButtonDefaultElement> = {}

  if (Element === "button") {
    buttons_only_props.disabled = disabled
    buttons_only_props.type = type
  }

  const a_only_props: ComponentPropsWithoutRef<"a"> = {}
  if (Element === "a") {
    a_only_props["role"] = "button"
  }

  const handleClick = (evt: ReactMouseEvent<T>) => {
    const _e = evt
    if (disabled) {
      evt.preventDefault()
    } else {
      onClick?.(_e)
    }
  }

  return (
    <Element
      ref={ref}
      className={cn}
      data-color={color}
      data-variant={variant}
      data-weight={weight}
      aria-disabled={disabled}
      {...buttons_only_props}
      {...a_only_props}
      {...rest}
      onClick={handleClick}
    >
      <span className={"label"}>
        {startIcon ? (
          <span className={"icon icon-start"}>{startIcon}</span>
        ) : null}
        <span className={"text"}>{children}</span>
        {endIcon ? <span className={"icon icon-end"}>{endIcon}</span> : null}
      </span>
    </Element>
  )
})

export default Button
